import React, { useCallback, useMemo, useState } from 'react';
import Dropdown from '../../dropdown/mainDropdown';
import DropdownItem from '../../dropdown/dropdownItem';
import { rootStore } from '../../../mobx/rootStore';
import { getRawComment, isEmpty } from '../../../helpers/common';
import { createNewComment, updateTaskDetails } from '../../../services/taskServices';
import { CHIP_SIZE, CHIP_TYPE, COMMENT_TYPE } from '../../../global/constants';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { TASK_ANALYTICS } from '../../../global/analyticsConstants';
import { nanoid } from 'nanoid';
import { Milestone } from '../../../mobx/interfaces/project';
import Chip from '../../chipComponent';

function MilestoneDropdown() {
  //State variable
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  //Mobx state variables
  const { taskStore, currentUser, projectStore } = rootStore;
  const { currentTask: taskDetails } = taskStore;
  const { currentProject } = projectStore;

  const removeTaskMilestone = useCallback(async () => {
    if (!isEmpty(taskDetails?.milestoneId)) {
      taskDetails?.setMilestone(null);
      const payloadTask = { milestoneIdUnset: true, projectId: taskDetails?.projectId };
      await updateTaskDetails(taskDetails?.id, payloadTask);
    }
  }, [taskDetails]);

  const updateTaskMilestone = useCallback(
    async (task: Milestone) => {
      if (!currentUser) return;
      const updatedComments = {
        id: nanoid(),
        type: COMMENT_TYPE.SET_MILESTONE,
        group: task?.milestoneName
      };
      taskDetails?.setMilestone(task?.id);
      const updatedComment = getRawComment(updatedComments, currentUser);
      taskDetails?.upsertComment(updatedComment);
      const payloadTask = { milestoneId: task?.id, projectId: taskDetails?.projectId };
      const result = await updateTaskDetails(taskDetails?.id, payloadTask);
      const commentResult = await createNewComment(taskDetails?.id, {
        Type: COMMENT_TYPE.SET_MILESTONE,
        Group: task?.milestoneName
      });
      if (result && commentResult) {
        trackAnalyticActivity(TASK_ANALYTICS.MILESTONE_SELECTED);
      }
    },
    [currentUser, taskDetails]
  );

  // render milestone data
  const renderMilestoneData = useMemo(() => {
    return (
      <>
        <DropdownItem key='no-milestone' label='No Milestone' onClick={removeTaskMilestone} />
        {taskDetails?.project?.milestones?.map((mileStone) => (
          <DropdownItem
            key={mileStone.id}
            label={mileStone?.milestoneName}
            isSelected={String(taskDetails?.milestoneId) === mileStone?.id}
            onClick={() => {
              if (String(taskDetails?.milestoneId) === mileStone?.id) {
                removeTaskMilestone();
              } else {
                updateTaskMilestone(mileStone);
              }
            }}
          />
        ))}
      </>
    );
  }, [removeTaskMilestone, taskDetails?.milestoneId, taskDetails?.project?.milestones, updateTaskMilestone]);

  const getMilestoneName = useCallback(() => {
    const milestoneName = currentProject?.milestones?.find((item) => item?.id === taskDetails?.milestoneId);
    return milestoneName?.milestoneName;
  }, [currentProject?.milestones, taskDetails?.milestoneId]);

  return (
    <Dropdown
      content={
        <Chip
          size={CHIP_SIZE.MEDIUM}
          title={taskDetails?.milestoneId && !isEmpty(getMilestoneName()) ? getMilestoneName() : 'Select Milestone'}
          leadingIconName='milestone-icon'
          variant={CHIP_TYPE.EMPTY}
          isActive={isDropdownOpen}
        />
      }
      trigger='click'
      modalTitle='Milestones'
      updateDropdownOpen={(isOpen) => setIsDropdownOpen(isOpen)}>
      {renderMilestoneData}
    </Dropdown>
  );
}

export default MilestoneDropdown;
